import { useState, useEffect } from "react";
import {
  MapPin, Plus, Edit2, Trash2, X, ToggleLeft, ToggleRight,
  Clock, ShoppingCart, DollarSign, RefreshCw,
} from "lucide-react";
import { useAdmin } from "../admin-context";

const Drawer = ({ open, onClose, title, children }) => {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-40 flex">
      <div className="flex-1 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="w-full max-w-md bg-white h-full shadow-2xl flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <h2 className="text-lg font-bold text-gray-900">{title}</h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full"><X size={18} /></button>
        </div>
        <div className="flex-1 overflow-y-auto p-6">{children}</div>
      </div>
    </div>
  );
};

const EMPTY_FORM = { name: "", description: "", delivery_fee: "", min_order: "", estimated_time: "30-45 min", status: "active" };

const DeliveryZonesPage = () => {
  const { deliveryZones, loadDeliveryZones, createDeliveryZone, updateDeliveryZone, deleteDeliveryZone, showToast } = useAdmin();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  useEffect(() => { loadDeliveryZones(); }, []);

  const openAdd = () => { setEditing(null); setForm(EMPTY_FORM); setDrawerOpen(true); };
  const openEdit = (zone) => { setEditing(zone); setForm({ ...zone }); setDrawerOpen(true); };

  const handleSave = async () => {
    if (!form.name.trim()) return showToast("Zone name is required", "error");
    setSaving(true);
    if (editing) {
      await updateDeliveryZone(editing.id, form);
      showToast("Zone updated");
    } else {
      await createDeliveryZone(form);
      showToast("Zone created");
    }
    setSaving(false);
    setDrawerOpen(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this delivery zone?")) return;
    await deleteDeliveryZone(id);
    showToast("Deleted");
  };

  const handleToggle = async (zone) => {
    await updateDeliveryZone(zone.id, { status: zone.status === "active" ? "inactive" : "active" });
    showToast(`Zone ${zone.status === "active" ? "disabled" : "enabled"}`);
  };

  const zones = deliveryZones || [];
  const activeCount = zones.filter((z) => z.status === "active").length;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap gap-3 items-center justify-between">
        <div>
          <h1 className="text-2xl font-extrabold text-gray-900">Delivery Zones</h1>
          <p className="text-sm text-gray-500 mt-0.5">Configure delivery areas, fees, and ETA</p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => loadDeliveryZones()} className="flex items-center gap-1.5 px-3 py-2 border rounded-lg text-sm text-gray-600 hover:bg-gray-50">
            <RefreshCw size={15} /> Refresh
          </button>
          <button onClick={openAdd} className="flex items-center gap-1.5 px-4 py-2 bg-orange-500 text-white rounded-lg text-sm font-semibold hover:bg-orange-600">
            <Plus size={16} /> Add Zone
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-white rounded-xl border p-4 flex items-center gap-3">
          <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center"><MapPin size={20} className="text-blue-600" /></div>
          <div>
            <p className="text-xs text-gray-500">Total Zones</p>
            <p className="text-2xl font-bold text-gray-900">{zones.length}</p>
          </div>
        </div>
        <div className="bg-white rounded-xl border p-4 flex items-center gap-3">
          <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center"><MapPin size={20} className="text-green-600" /></div>
          <div>
            <p className="text-xs text-gray-500">Active Zones</p>
            <p className="text-2xl font-bold text-green-700">{activeCount}</p>
          </div>
        </div>
        <div className="bg-white rounded-xl border p-4 flex items-center gap-3">
          <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center"><DollarSign size={20} className="text-orange-500" /></div>
          <div>
            <p className="text-xs text-gray-500">Avg. Delivery Fee</p>
            <p className="text-2xl font-bold text-gray-900">
              {zones.length ? `$${(zones.reduce((s, z) => s + Number(z.delivery_fee || 0), 0) / zones.length).toFixed(2)}` : "—"}
            </p>
          </div>
        </div>
      </div>

      {/* Zone cards */}
      {zones.length === 0 ? (
        <div className="bg-white rounded-xl border p-12 text-center text-gray-400">
          <MapPin size={40} className="mx-auto mb-3 opacity-30" />
          <p className="font-medium">No delivery zones yet</p>
          <p className="text-sm mt-1">Add your first zone to get started</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {zones.map((zone) => (
            <div key={zone.id} className={`bg-white rounded-xl border p-5 flex flex-col gap-3 transition ${zone.status === "inactive" ? "opacity-60" : ""}`}>
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-2">
                  <div className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${zone.status === "active" ? "bg-green-500" : "bg-gray-400"}`} />
                  <h3 className="font-bold text-gray-900">{zone.name}</h3>
                </div>
                <div className="flex items-center gap-1">
                  <button onClick={() => openEdit(zone)} className="p-1.5 hover:bg-gray-100 rounded-lg text-gray-500"><Edit2 size={14} /></button>
                  <button onClick={() => handleDelete(zone.id)} className="p-1.5 hover:bg-red-50 rounded-lg text-red-400"><Trash2 size={14} /></button>
                </div>
              </div>
              {zone.description && <p className="text-xs text-gray-500 line-clamp-2">{zone.description}</p>}
              <div className="grid grid-cols-3 gap-2 text-xs">
                <div className="bg-gray-50 rounded-lg p-2 text-center">
                  <DollarSign size={12} className="mx-auto mb-1 text-orange-500" />
                  <p className="font-bold text-gray-900">${Number(zone.delivery_fee || 0).toFixed(2)}</p>
                  <p className="text-gray-400">Fee</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-2 text-center">
                  <ShoppingCart size={12} className="mx-auto mb-1 text-blue-500" />
                  <p className="font-bold text-gray-900">${Number(zone.min_order || 0).toFixed(2)}</p>
                  <p className="text-gray-400">Min order</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-2 text-center">
                  <Clock size={12} className="mx-auto mb-1 text-purple-500" />
                  <p className="font-bold text-gray-900 text-[10px] leading-tight">{zone.estimated_time}</p>
                  <p className="text-gray-400">ETA</p>
                </div>
              </div>
              <button onClick={() => handleToggle(zone)}
                className={`flex items-center gap-2 text-xs font-medium py-1.5 px-3 rounded-lg border transition ${
                  zone.status === "active"
                    ? "border-gray-200 text-gray-600 hover:bg-gray-50"
                    : "border-green-200 text-green-700 hover:bg-green-50"
                }`}>
                {zone.status === "active" ? <ToggleRight size={14} /> : <ToggleLeft size={14} />}
                {zone.status === "active" ? "Disable Zone" : "Enable Zone"}
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Form Drawer */}
      <Drawer open={drawerOpen} onClose={() => setDrawerOpen(false)} title={editing ? "Edit Zone" : "Add Delivery Zone"}>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Zone Name <span className="text-red-500">*</span></label>
            <input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              placeholder="e.g. Downtown, North Zone"
              className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-300" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <textarea value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
              rows={2} placeholder="Describe the coverage area..."
              className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-300 resize-none" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Delivery Fee ($)</label>
              <input type="number" min="0" step="0.01" value={form.delivery_fee}
                onChange={(e) => setForm((f) => ({ ...f, delivery_fee: e.target.value }))}
                placeholder="0.00"
                className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-300" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Min. Order ($)</label>
              <input type="number" min="0" step="0.01" value={form.min_order}
                onChange={(e) => setForm((f) => ({ ...f, min_order: e.target.value }))}
                placeholder="0.00"
                className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-300" />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Estimated Delivery Time</label>
            <input value={form.estimated_time} onChange={(e) => setForm((f) => ({ ...f, estimated_time: e.target.value }))}
              placeholder="e.g. 30-45 min"
              className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-300" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
            <select value={form.status} onChange={(e) => setForm((f) => ({ ...f, status: e.target.value }))}
              className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-300">
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
          <button onClick={handleSave} disabled={saving}
            className="w-full py-2.5 bg-orange-500 hover:bg-orange-600 text-white font-semibold rounded-lg transition disabled:opacity-60">
            {saving ? "Saving..." : editing ? "Update Zone" : "Create Zone"}
          </button>
        </div>
      </Drawer>
    </div>
  );
};

export default DeliveryZonesPage;
